import AddIcon from '@mui/icons-material/AddRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import { FormHelperText, Grid, ToggleButton, Typography } from '@mui/material';
import { FieldInputProps, FieldRenderProps } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from '../../modules/translation/TranslationContext';
import Field from './Field';

export type ToggleButtonOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

type RFFToggleButtonListProps = {
  name: string;
  items: ToggleButtonOption[];
};

const normalizeValue = (value?: string[] | string): string[] => {
  if (!value || !Array.isArray(value)) {
    return [];
  }

  return value;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: `${theme.spacing(2)} !important`,
    fontSize: `${theme.typography.caption.fontSize} !important`,
    height: `${theme.spacing(4)} !important`,
    textTransform: 'lowercase',
    color: `${theme.palette.text.secondary} !important`,
    border: `1px solid rgba(0, 0, 0, 0.23) !important`,
  },
  selected: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    borderColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.common.white} !important`,
  },
}));
/**
 * @deprecated React Final Form has been deprecated in the system.
 * Components based on RFF are no longer supported.
 * Please use <ToggleButton /> or other updated alternatives.
 */
const RFFToggleButtonList = ({ name, items }: RFFToggleButtonListProps) => {
  const { classes } = useStyles();
  const { translate } = useTranslation();

  const handleToggleSelect = (
    item: ToggleButtonOption,
    isSelected: boolean,
    input: FieldInputProps<string[], HTMLElement>,
  ) => {
    const selecteds: string[] = normalizeValue(input.value);

    let newSelecteds: string[];
    if (isSelected) {
      newSelecteds = selecteds.filter((selectedItemId) => selectedItemId !== item.value);
    } else {
      newSelecteds = [...selecteds, item.value];
    }

    input.onChange(newSelecteds);
  };

  return (
    <Field name={name}>
      {({ input, meta }: FieldRenderProps<string[], HTMLElement>) => {
        const showError =
          ((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched;

        const selecteds = normalizeValue(input.value);

        return (
          <>
            {showError && (
              <FormHelperText error={showError} id="helper-text">
                {translate(meta.error)}
              </FormHelperText>
            )}
            <Grid container spacing={1}>
              {items.map((item) => {
                const selected = selecteds.includes(item.value);

                return (
                  <Grid size="auto">
                    <ToggleButton
                      key={item.value}
                      value={item.value}
                      selected={selected}
                      onChange={() =>
                        handleToggleSelect(item, selecteds.includes(item.value), input)
                      }
                      classes={classes}
                    >
                      {selected ? (
                        <CheckIcon fontSize="small" style={{ marginRight: 8 }} />
                      ) : (
                        <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                      )}
                      <Typography align="center">{item.label}</Typography>
                    </ToggleButton>
                  </Grid>
                );
              })}
            </Grid>
          </>
        );
      }}
    </Field>
  );
};

export default RFFToggleButtonList;
